import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDidMount, useShowHide, useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { clearUserInput, saveUserInput, useUserInput } from '@store';
import { EnterpriseSolutionDetail } from '@types';
import { modals } from '@modals';
import { useDrawer, useFetchDocument } from '@hooks';
import { BoxRef, TooltipRef } from '@bcp-nextgen-dx-component-factory/accolade-design-system';

export interface EnterpriseSolutionHelperOutput {
	resources: EnterpriseSolutionResources;
	anchorRef: React.RefObject<BoxRef>;
	tooltipRef: React.RefObject<TooltipRef>;
	generalConditionsDrawerName: string;
	base64Conditions?: string;
	seeAllAdvantages: boolean;
	handleOnClickSelectSolution: (solution: EnterpriseSolutionDetail) => void;
	handleChangeViewAdvantages: (seeAllAdvantages: boolean | undefined) => void;
	handleShowModalBenefit: () => void;
	handleGeneralConditions: () => void;
}

export interface EnterpriseSolutionResources {
	title: string;
	subtitle: string;
	tooltip: string;
	cardTitle: string;
	addStampDuty: string;
	generalConditions: string;
	openAdvantages: string;
	closeAdvantages: string;
	publicityPhrase: string;
	attentionRead: string;
}

export const useEnterpriseSolutionHelper = (): EnterpriseSolutionHelperOutput => {
	const t = useTranslation();
	const dispatch = useDispatch();

	const generalConditionsDrawerName = 'generalConditions';
	const [seeAllAdvantages, setSeeAllAdvantages] = useState(false);
	const { show } = useShowHide(modals.allBenefits);
	const { show: showPdfDrawer } = useDrawer(generalConditionsDrawerName);
	const { base64BankDocument: base64Conditions, fetchBankDocument } = useFetchDocument({
		reference: t('solutions.document.conditionsTerms.id'),
	});
	const { solutionId } = useUserInput();

	const anchorRef = useRef<BoxRef>(null);
	const tooltipRef = useRef<TooltipRef>(null);

	const resources = React.useMemo((): EnterpriseSolutionResources => {
		return {
			title: t('placeholders.enterpriseSolution.title'),
			subtitle: t('placeholders.enterpriseSolution.subtitle'),
			tooltip: t('placeholders.enterpriseSolution.tooltip'),
			cardTitle: t('placeholders.enterpriseSolution.card.title'),
			addStampDuty: t('general.addStampDuty'),
			generalConditions: t('placeholders.enterpriseSolution.card.generalConditions'),
			openAdvantages: t('placeholders.enterpriseSolution.openAdvantages'),
			closeAdvantages: t('placeholders.enterpriseSolution.closeAdvantages'),
			publicityPhrase: t('placeholders.enterpriseSolution.publicityPhrase'),
			attentionRead: t('general.pdfViewer.attentionRead'),
		};
	}, [t]);

	useDidMount(() => {
		fetchBankDocument();
	});

	const handleOnClickSelectSolution = React.useCallback(
		(solution: EnterpriseSolutionDetail) => {
			if (solutionId !== undefined && solutionId !== solution.id) {
				dispatch(clearUserInput());
			}
			dispatch(saveUserInput({ solutionId: solution.id }));
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dispatch]
	);

	const handleChangeViewAdvantages = React.useCallback((seeAllAdvantages: boolean | undefined) => {
		if (seeAllAdvantages !== undefined) {
			setSeeAllAdvantages(!seeAllAdvantages);
		}
	}, []);

	const handleShowModalBenefit = React.useCallback(() => {
		show();
	}, [show]);

	const handleGeneralConditions = () => {
		showPdfDrawer();
	};

	return {
		resources,
		anchorRef,
		tooltipRef,
		generalConditionsDrawerName,
		base64Conditions,
		seeAllAdvantages,
		handleOnClickSelectSolution,
		handleChangeViewAdvantages,
		handleShowModalBenefit,
		handleGeneralConditions,
	};
};
